import React, { useEffect, useState, useRef } from "react";
import { api, authHeaders } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, ShieldAlert, Clock, Loader2, Upload, FileCheck2, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const DOCS = [
  { type: "business", label: "Visura camerale / documento aziendale", required: true },
  { type: "id_front", label: "Documento del titolare (fronte)", required: true },
  { type: "id_back", label: "Documento del titolare (retro)", required: false },
];

const emptyForm = {
  ragione_sociale: "", partita_iva: "", codice_fiscale: "", forma_giuridica: "",
  sede_indirizzo: "", sede_citta: "", sede_cap: "", sede_provincia: "",
  titolare_nome: "", titolare_cognome: "", titolare_cf: "",
};

export default function VendorVerification() {
  const { refreshVendor } = useAuth();
  const [ver, setVer] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [uploading, setUploading] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const fileRefs = { business: useRef(), id_front: useRef(), id_back: useRef() };

  const load = async () => {
    try {
      const r = await api.get("/vendor/verification", { headers: authHeaders("vendor") });
      setVer(r.data.verification);
      const c = r.data.verification.company || {};
      const o = r.data.verification.owner || {};
      if (c.ragione_sociale || o.nome) {
        setForm({
          ragione_sociale: c.ragione_sociale || "", partita_iva: c.partita_iva || "",
          codice_fiscale: c.codice_fiscale || "", forma_giuridica: c.forma_giuridica || "",
          sede_indirizzo: c.sede?.indirizzo || "", sede_citta: c.sede?.citta || "",
          sede_cap: c.sede?.cap || "", sede_provincia: c.sede?.provincia || "",
          titolare_nome: o.nome || "", titolare_cognome: o.cognome || "", titolare_cf: o.codice_fiscale || "",
        });
      }
    } catch (_) {}
  };
  useEffect(() => { load(); }, []);

  const uploadDoc = async (type, file) => {
    if (!file) return;
    setUploading(type);
    const fd = new FormData();
    fd.append("doc_type", type);
    fd.append("file", file);
    try {
      await api.post("/vendor/verification/document", fd, { headers: { ...authHeaders("vendor"), "Content-Type": "multipart/form-data" } });
      toast.success("Documento caricato");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Caricamento non riuscito");
    } finally { setUploading(""); }
  };

  const submit = async () => {
    for (const f of ["ragione_sociale", "partita_iva", "sede_indirizzo", "sede_citta", "titolare_nome", "titolare_cognome"]) {
      if (!form[f].trim()) { toast.error("Compila tutti i campi obbligatori"); return; }
    }
    setSubmitting(true);
    try {
      const r = await api.post("/vendor/verification/submit", form, { headers: authHeaders("vendor") });
      setVer(r.data.verification);
      await refreshVendor();
      if (r.data.auto_approved) toast.success("Verifica superata! Ora puoi pubblicare offerte.");
      else toast.success("Documenti inviati: la verifica è in corso.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invio non riuscito");
    } finally { setSubmitting(false); }
  };

  if (!ver) return <div className="py-16 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#E63946]" /></div>;

  const uploadedTypes = new Set((ver.documents || []).map((d) => d.type));
  const status = ver.status;
  const needResubmit = new Set(status === "rejected" ? (ver.resubmit_docs || []) : []);

  const StatusBanner = () => {
    if (status === "approved") return (
      <div className="kn-card p-4 flex items-center gap-3 bg-emerald-50 border border-emerald-100" data-testid="verif-status-approved">
        <ShieldCheck className="h-6 w-6 text-emerald-600 shrink-0" />
        <div><p className="font-bold text-emerald-800">Account verificato</p><p className="text-sm text-emerald-700">Puoi pubblicare offerte visibili ai clienti.</p></div>
      </div>
    );
    if (status === "submitted") return (
      <div className="kn-card p-4 flex items-center gap-3 bg-amber-50 border border-amber-100" data-testid="verif-status-submitted">
        <Clock className="h-6 w-6 text-amber-600 shrink-0" />
        <div><p className="font-bold text-amber-800">Verifica in corso</p><p className="text-sm text-amber-700">Stiamo controllando i tuoi documenti. Riceverai una email con l'esito.</p></div>
      </div>
    );
    if (status === "rejected") return (
      <div className="kn-card p-4 flex items-start gap-3 bg-red-50 border border-red-100" data-testid="verif-status-rejected">
        <ShieldAlert className="h-6 w-6 text-red-600 shrink-0" />
        <div><p className="font-bold text-red-800">Verifica non superata</p><p className="text-sm text-red-700">{ver.reject_reason || "Documenti non conformi."} Correggi e reinvia.</p></div>
      </div>
    );
    return (
      <div className="kn-card p-4 flex items-start gap-3 bg-slate-50" data-testid="verif-status-unverified">
        <AlertTriangle className="h-6 w-6 text-[#E63946] shrink-0" />
        <div><p className="font-bold text-slate-800">Verifica la tua identità</p><p className="text-sm text-slate-600">Per pubblicare offerte devi confermare l'identità della tua azienda. Serve a impedire iscrizioni non autorizzate.</p></div>
      </div>
    );
  };

  const readOnly = status === "submitted" || status === "approved";

  return (
    <div className="space-y-5 max-w-2xl" data-testid="vendor-verification-page">
      <div>
        <h1 className="text-2xl font-extrabold text-slate-900">Verifica identità</h1>
        <p className="text-slate-500 text-sm mt-1">Dati aziendali e documenti del titolare.</p>
      </div>
      <StatusBanner />

      {status !== "approved" && (
        <>
          {/* Documents */}
          <div className="kn-card p-4 space-y-3">
            <p className="font-bold text-slate-800">Documenti</p>
            {DOCS.map((d) => {
              const done = uploadedTypes.has(d.type);
              return (
                <div key={d.type} className="flex items-center gap-3">
                  <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${done ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-400"}`}>
                    {done ? <FileCheck2 className="h-5 w-5" /> : <Upload className="h-5 w-5" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5 flex-wrap">
                      {d.label}{d.required && <span className="text-[#E63946]">*</span>}
                      {needResubmit.has(d.type) && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-50 text-red-600" data-testid={`verif-resubmit-${d.type}`}>Da reinviare</span>}
                    </p>
                    <p className="text-xs text-slate-400">{done ? "Caricato" : "JPG, PNG o PDF (max 12MB)"}</p>
                  </div>
                  <input ref={fileRefs[d.type]} type="file" accept="image/*,application/pdf" className="hidden"
                    data-testid={`verif-file-${d.type}`}
                    onChange={(e) => uploadDoc(d.type, e.target.files?.[0])} disabled={readOnly} />
                  <Button type="button" variant="outline" disabled={readOnly || uploading === d.type} onClick={() => fileRefs[d.type].current?.click()}
                    data-testid={`verif-upload-${d.type}`} className="h-9 rounded-xl font-bold shrink-0">
                    {uploading === d.type ? <Loader2 className="h-4 w-4 animate-spin" /> : (done ? "Sostituisci" : "Carica")}
                  </Button>
                </div>
              );
            })}
          </div>

          {/* Company data */}
          <div className="kn-card p-4 space-y-3">
            <p className="font-bold text-slate-800">Dati aziendali</p>
            <F label="Ragione sociale *"><Input data-testid="verif-ragione-sociale" value={form.ragione_sociale} disabled={readOnly} onChange={(e) => setForm({ ...form, ragione_sociale: e.target.value })} className="h-11 rounded-xl" /></F>
            <div className="grid grid-cols-2 gap-3">
              <F label="Partita IVA *"><Input data-testid="verif-piva" value={form.partita_iva} disabled={readOnly} onChange={(e) => setForm({ ...form, partita_iva: e.target.value })} className="h-11 rounded-xl" /></F>
              <F label="Codice Fiscale azienda"><Input value={form.codice_fiscale} disabled={readOnly} onChange={(e) => setForm({ ...form, codice_fiscale: e.target.value })} className="h-11 rounded-xl" /></F>
            </div>
            <F label="Forma giuridica"><Input value={form.forma_giuridica} disabled={readOnly} placeholder="Es. SRL, SNC, Ditta individuale" onChange={(e) => setForm({ ...form, forma_giuridica: e.target.value })} className="h-11 rounded-xl" /></F>
            <F label="Sede legale — indirizzo *"><Input data-testid="verif-sede-indirizzo" value={form.sede_indirizzo} disabled={readOnly} onChange={(e) => setForm({ ...form, sede_indirizzo: e.target.value })} className="h-11 rounded-xl" /></F>
            <div className="grid grid-cols-3 gap-3">
              <F label="Città *"><Input data-testid="verif-citta" value={form.sede_citta} disabled={readOnly} onChange={(e) => setForm({ ...form, sede_citta: e.target.value })} className="h-11 rounded-xl" /></F>
              <F label="CAP"><Input value={form.sede_cap} disabled={readOnly} onChange={(e) => setForm({ ...form, sede_cap: e.target.value })} className="h-11 rounded-xl" /></F>
              <F label="Prov."><Input value={form.sede_provincia} disabled={readOnly} onChange={(e) => setForm({ ...form, sede_provincia: e.target.value })} className="h-11 rounded-xl" /></F>
            </div>
          </div>

          {/* Owner data */}
          <div className="kn-card p-4 space-y-3">
            <p className="font-bold text-slate-800">Titolare / Legale rappresentante</p>
            <div className="grid grid-cols-2 gap-3">
              <F label="Nome *"><Input data-testid="verif-titolare-nome" value={form.titolare_nome} disabled={readOnly} onChange={(e) => setForm({ ...form, titolare_nome: e.target.value })} className="h-11 rounded-xl" /></F>
              <F label="Cognome *"><Input data-testid="verif-titolare-cognome" value={form.titolare_cognome} disabled={readOnly} onChange={(e) => setForm({ ...form, titolare_cognome: e.target.value })} className="h-11 rounded-xl" /></F>
            </div>
            <F label="Codice Fiscale titolare"><Input value={form.titolare_cf} disabled={readOnly} onChange={(e) => setForm({ ...form, titolare_cf: e.target.value })} className="h-11 rounded-xl" /></F>
          </div>

          {!readOnly && (
            <Button onClick={submit} disabled={submitting} data-testid="verif-submit" className="w-full h-12 rounded-xl font-bold gap-2">
              {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />} Invia per la verifica
            </Button>
          )}
        </>
      )}
    </div>
  );
}

function F({ label, children }) {
  return <div className="space-y-1.5"><Label>{label}</Label>{children}</div>;
}
